import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { 
  Lock, Calendar, FileText, Mail, Eye, Trash2, 
  CheckCircle, Clock, AlertCircle, LogOut
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  pending: { bg: "bg-yellow-100", text: "text-yellow-700", icon: Clock },
  confirmed: { bg: "bg-green-100", text: "text-green-700", icon: CheckCircle },
  completed: { bg: "bg-blue-100", text: "text-blue-700", icon: CheckCircle },
  cancelled: { bg: "bg-red-100", text: "text-red-700", icon: AlertCircle },
  new: { bg: "bg-sky-500/20", text: "text-sky-700", icon: AlertCircle },
  in_progress: { bg: "bg-yellow-100", text: "text-yellow-700", icon: Clock },
  quoted: { bg: "bg-green-100", text: "text-green-700", icon: CheckCircle },
};

const statusLabels = {
  pending: "En attente",
  confirmed: "Confirmé",
  completed: "Terminé",
  cancelled: "Annulé",
  new: "Nouveau",
  in_progress: "En cours",
  quoted: "Devis envoyé",
};

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selectedQuote, setSelectedQuote] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      
      if (response.ok) {
        setIsAuthenticated(true);
        fetchData();
        toast.success("Connexion réussie !");
      } else {
        toast.error("Mot de passe incorrect");
      }
    } catch (error) {
      toast.error("Erreur de connexion");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const [statsRes, appointmentsRes, quotesRes, contactsRes] = await Promise.all([
        fetch(`${API}/dashboard/stats`),
        fetch(`${API}/appointments`),
        fetch(`${API}/quotes`),
        fetch(`${API}/contacts`),
      ]);

      setStats(await statsRes.json());
      setAppointments(await appointmentsRes.json());
      setQuotes(await quotesRes.json());
      setContacts(await contactsRes.json());
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    }
  };

  const updateStatus = async (type, id, status) => {
    try {
      await fetch(`${API}/${type}/${id}?status=${status}`, { method: "PATCH" });
      fetchData();
      toast.success("Statut mis à jour");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const deleteItem = async (type, id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cet élément ?")) return;
    try {
      await fetch(`${API}/${type}/${id}`, { method: "DELETE" });
      fetchData();
      toast.success("Élément supprimé");
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const StatusBadge = ({ status }) => {
    const config = statusColors[status] || statusColors.pending;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon className="w-3 h-3" />
        {statusLabels[status] || status}
      </span>
    );
  };

  if (!isAuthenticated) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-slate-900" data-testid="admin-login">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="glass-card p-8 rounded-3xl w-full max-w-md mx-4"
        >
          <div className="w-16 h-16 bg-sky-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Lock className="w-8 h-8 text-sky-400" />
          </div>
          <h1 className="text-2xl font-bold text-white text-center mb-2">
            Espace Administrateur
          </h1>
          <p className="text-slate-500 text-center mb-8">
            Connectez-vous pour accéder au dashboard
          </p>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="password" className="mb-2 block">Mot de passe</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="bg-slate-800/50"
                data-testid="admin-password"
              />
            </div>
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-sky-500 hover:bg-sky-600 text-white rounded-full py-6"
              data-testid="admin-login-btn"
            >
              {isLoading ? "Connexion..." : "Se connecter"}
            </Button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-slate-900" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">Dashboard</h1>
            <p className="text-slate-500">Gestion des rendez-vous et devis</p>
          </div>
          <Button
            variant="outline"
            onClick={() => setIsAuthenticated(false)}
            className="border-slate-300"
            data-testid="admin-logout"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Déconnexion
          </Button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <div className="glass-card p-5 rounded-2xl">
              <p className="text-sm text-slate-500 mb-1">Rendez-vous</p>
              <p className="text-2xl font-bold text-white">{stats.total_appointments}</p>
            </div>
            <div className="glass-card p-5 rounded-2xl">
              <p className="text-sm text-slate-500 mb-1">En attente</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.pending_appointments}</p>
            </div>
            <div className="glass-card p-5 rounded-2xl">
              <p className="text-sm text-slate-500 mb-1">Devis</p>
              <p className="text-2xl font-bold text-white">{stats.total_quotes}</p>
            </div>
            <div className="glass-card p-5 rounded-2xl">
              <p className="text-sm text-slate-500 mb-1">Nouveaux devis</p>
              <p className="text-2xl font-bold text-sky-400">{stats.new_quotes}</p>
            </div>
            <div className="glass-card p-5 rounded-2xl">
              <p className="text-sm text-slate-500 mb-1">Messages</p>
              <p className="text-2xl font-bold text-white">{stats.total_contacts}</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="appointments" className="space-y-6">
          <TabsList className="bg-white/70 backdrop-blur-sm p-1 rounded-xl">
            <TabsTrigger value="appointments" className="rounded-lg data-[state=active]:bg-sky-500 data-[state=active]:text-white">
              <Calendar className="w-4 h-4 mr-2" />
              Rendez-vous ({appointments.length})
            </TabsTrigger>
            <TabsTrigger value="quotes" className="rounded-lg data-[state=active]:bg-sky-500 data-[state=active]:text-white">
              <FileText className="w-4 h-4 mr-2" />
              Devis ({quotes.length})
            </TabsTrigger>
            <TabsTrigger value="contacts" className="rounded-lg data-[state=active]:bg-sky-500 data-[state=active]:text-white">
              <Mail className="w-4 h-4 mr-2" />
              Messages ({contacts.length})
            </TabsTrigger>
          </TabsList>

          {/* Appointments Tab */}
          <TabsContent value="appointments">
            <div className="glass-card rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-900/80">
                    <tr>
                      <th className="text-left p-4 text-sm font-semibold text-slate-300">Client</th>
                      <th className="text-left p-4 text-sm font-semibold text-slate-300">Service</th>
                      <th className="text-left p-4 text-sm font-semibold text-slate-300">Date</th>
                      <th className="text-left p-4 text-sm font-semibold text-slate-300">Heure</th>
                      <th className="text-left p-4 text-sm font-semibold text-slate-300">Statut</th>
                      <th className="text-left p-4 text-sm font-semibold text-slate-300">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {appointments.map((appt) => (
                      <tr key={appt.id} className="hover:bg-slate-900/50">
                        <td className="p-4">
                          <p className="font-medium text-white">{appt.name}</p>
                          <p className="text-sm text-slate-500">{appt.phone}</p>
                        </td>
                        <td className="p-4 text-sm text-slate-300">{appt.service}</td>
                        <td className="p-4 text-sm text-slate-300">{appt.date}</td>
                        <td className="p-4 text-sm text-slate-300">{appt.time}</td>
                        <td className="p-4">
                          <StatusBadge status={appt.status} />
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <Select
                              value={appt.status}
                              onValueChange={(value) => updateStatus("appointments", appt.id, value)}
                            >
                              <SelectTrigger className="w-32 h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">En attente</SelectItem>
                                <SelectItem value="confirmed">Confirmé</SelectItem>
                                <SelectItem value="completed">Terminé</SelectItem>
                                <SelectItem value="cancelled">Annulé</SelectItem>
                              </SelectContent>
                            </Select>
                            <button
                              onClick={() => deleteItem("appointments", appt.id)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {appointments.length === 0 && (
                      <tr>
                        <td colSpan="6" className="p-8 text-center text-slate-500">
                          Aucun rendez-vous pour le moment
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Quotes Tab */}
          <TabsContent value="quotes">
            <div className="glass-card rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-900/80">
                    <tr>
                      <th className="text-left p-4 text-sm font-semibold text-slate-300">Client</th>
                      <th className="text-left p-4 text-sm font-semibold text-slate-300">Véhicule</th>
                      <th className="text-left p-4 text-sm font-semibold text-slate-300">Vitrage</th>
                      <th className="text-left p-4 text-sm font-semibold text-slate-300">Photos</th>
                      <th className="text-left p-4 text-sm font-semibold text-slate-300">Statut</th>
                      <th className="text-left p-4 text-sm font-semibold text-slate-300">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {quotes.map((quote) => (
                      <tr key={quote.id} className="hover:bg-slate-900/50">
                        <td className="p-4">
                          <p className="font-medium text-white">{quote.name}</p>
                          <p className="text-sm text-slate-500">{quote.phone}</p>
                        </td>
                        <td className="p-4">
                          <p className="text-sm text-white">{quote.marque} {quote.modele}</p>
                          <p className="text-xs text-slate-500">{quote.immatriculation}</p>
                        </td>
                        <td className="p-4 text-sm text-slate-300">{quote.type_vitrage}</td>
                        <td className="p-4">
                          {quote.photos?.length > 0 ? (
                            <button
                              onClick={() => setSelectedQuote(quote)}
                              className="flex items-center gap-1 text-sky-400 hover:text-sky-700"
                            >
                              <Eye className="w-4 h-4" />
                              <span className="text-sm">{quote.photos.length}</span>
                            </button>
                          ) : (
                            <span className="text-sm text-slate-400">Aucune</span>
                          )}
                        </td>
                        <td className="p-4">
                          <StatusBadge status={quote.status} />
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <Select
                              value={quote.status}
                              onValueChange={(value) => updateStatus("quotes", quote.id, value)}
                            >
                              <SelectTrigger className="w-32 h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="new">Nouveau</SelectItem>
                                <SelectItem value="in_progress">En cours</SelectItem>
                                <SelectItem value="quoted">Devis envoyé</SelectItem>
                                <SelectItem value="completed">Terminé</SelectItem>
                              </SelectContent>
                            </Select>
                            <button
                              onClick={() => deleteItem("quotes", quote.id)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {quotes.length === 0 && (
                      <tr>
                        <td colSpan="6" className="p-8 text-center text-slate-500">
                          Aucune demande de devis pour le moment
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts">
            <div className="glass-card rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-900/80">
                    <tr>
                      <th className="text-left p-4 text-sm font-semibold text-slate-300">Contact</th>
                      <th className="text-left p-4 text-sm font-semibold text-slate-300">Sujet</th>
                      <th className="text-left p-4 text-sm font-semibold text-slate-300">Message</th>
                      <th className="text-left p-4 text-sm font-semibold text-slate-300">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {contacts.map((contact) => (
                      <tr key={contact.id} className="hover:bg-slate-900/50">
                        <td className="p-4">
                          <p className="font-medium text-white">{contact.name}</p>
                          <p className="text-sm text-slate-500">{contact.email}</p>
                          <p className="text-sm text-slate-500">{contact.phone}</p>
                        </td>
                        <td className="p-4 text-sm text-slate-300">{contact.subject}</td>
                        <td className="p-4">
                          <p className="text-sm text-slate-300 max-w-xs truncate">{contact.message}</p>
                        </td>
                        <td className="p-4 text-sm text-slate-500">
                          {new Date(contact.created_at).toLocaleDateString('fr-FR')}
                        </td>
                      </tr>
                    ))}
                    {contacts.length === 0 && (
                      <tr>
                        <td colSpan="4" className="p-8 text-center text-slate-500">
                          Aucun message pour le moment
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Photo Modal */}
        {selectedQuote && (
          <div 
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedQuote(null)}
          >
            <div 
              className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-white mb-4">
                Photos - {selectedQuote.marque} {selectedQuote.modele}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {selectedQuote.photos.map((photo, index) => (
                  <img
                    key={index}
                    src={photo}
                    alt={`Photo ${index + 1}`}
                    className="w-full h-48 object-cover rounded-xl"
                  />
                ))}
              </div>
              <Button
                onClick={() => setSelectedQuote(null)}
                className="mt-6 w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full"
              >
                Fermer
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
